import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

export default function CollectionPerformance() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState({ startDate: '', endDate: '' });
  const [summary, setSummary] = useState({
    activeOfficers: 0,
    totalRecovered: 0,
    totalTransactions: 0,
    totalPrincipal: 0,
    totalInterest: 0,
    totalPenalty: 0,
  });

  useEffect(() => {
    fetchPerformance();
  }, [dateFilter]);

  const fetchPerformance = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFilter.startDate) params.append('startDate', dateFilter.startDate);
      if (dateFilter.endDate) params.append('endDate', dateFilter.endDate);

      const res = await api.get(`/collections/performance?${params}`);
      const perfData = res.data.data || [];
      setData(perfData);

      const totalRecovered = perfData.reduce((acc, o) => acc + (o.totalAmount || 0), 0);
      const totalTransactions = perfData.reduce((acc, o) => acc + (o.totalCollections || 0), 0);
      const totalPrincipal = perfData.reduce((acc, o) => acc + (o.totalPrincipal || 0), 0);
      const totalInterest = perfData.reduce((acc, o) => acc + (o.totalInterest || 0), 0);
      const totalPenalty = perfData.reduce((acc, o) => acc + (o.totalPenalty || 0), 0);

      setSummary({
        activeOfficers: perfData.filter(o => o.totalCollections > 0).length,
        totalRecovered,
        totalTransactions,
        totalPrincipal,
        totalInterest,
        totalPenalty,
      });
    } catch (err) {
      console.error('Failed to fetch performance data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="spinner"></div>;

  return (
    <div>
      <h3 className="mb-2">📊 Collection Performance</h3>

      {/* Date Filters */}
      <div className="flex gap-2 mb-2" style={{ alignItems: 'center' }}>
        <input type="date" className="form-control" style={{ maxWidth: 180 }} value={dateFilter.startDate}
          onChange={e => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))} />
        <span className="text-muted">to</span>
        <input type="date" className="form-control" style={{ maxWidth: 180 }} value={dateFilter.endDate}
          onChange={e => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))} />
        {(dateFilter.startDate || dateFilter.endDate) && (
          <button className="btn btn-sm btn-outline" onClick={() => setDateFilter({ startDate: '', endDate: '' })}>Clear</button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-6 gap-1 mb-2">
        <div className="card p-2 text-center" style={{ borderLeft: '4px solid #3b82f6' }}>
          <div className="text-xs text-muted">Active Officers</div>
          <div className="font-bold text-lg">{summary.activeOfficers}</div>
        </div>
        <div className="card p-2 text-center" style={{ borderLeft: '4px solid #059669' }}>
          <div className="text-xs text-muted">Total Recovered</div>
          <div className="font-bold text-lg" style={{ color: '#059669' }}>{formatCurrency(summary.totalRecovered)}</div>
        </div>
        <div className="card p-2 text-center" style={{ borderLeft: '4px solid #2563eb' }}>
          <div className="text-xs text-muted">Principal</div>
          <div className="font-bold text-lg" style={{ color: '#2563eb' }}>{formatCurrency(summary.totalPrincipal)}</div>
        </div>
        <div className="card p-2 text-center" style={{ borderLeft: '4px solid #f59e0b' }}>
          <div className="text-xs text-muted">Interest</div>
          <div className="font-bold text-lg" style={{ color: '#f59e0b' }}>{formatCurrency(summary.totalInterest)}</div>
        </div>
        <div className="card p-2 text-center" style={{ borderLeft: '4px solid #dc2626' }}>
          <div className="text-xs text-muted">Penalty</div>
          <div className="font-bold text-lg" style={{ color: '#dc2626' }}>{formatCurrency(summary.totalPenalty)}</div>
        </div>
        <div className="card p-2 text-center" style={{ borderLeft: '4px solid #8b5cf6' }}>
          <div className="text-xs text-muted">Transactions</div>
          <div className="font-bold text-lg">{summary.totalTransactions}</div>
        </div>
      </div>

      <div className="grid grid-2 gap-2">
        {/* Chart */}
        <div className="card">
          <h4 className="mb-1">Recovery Breakdown by Officer</h4>
          <div style={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={val => `${(val / 1000).toFixed(0)}k`} />
                <YAxis dataKey="officerName" type="category" width={150} />
                <Tooltip
                  formatter={(value, name) => [formatCurrency(value), name === 'totalPrincipal' ? 'Principal' : name === 'totalInterest' ? 'Interest' : 'Penalty']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #ccc' }}
                />
                <Legend formatter={value => value === 'totalPrincipal' ? 'Principal' : value === 'totalInterest' ? 'Interest' : 'Penalty'} />
                <Bar dataKey="totalPrincipal" stackId="a" fill="#2563eb" />
                <Bar dataKey="totalInterest" stackId="a" fill="#f59e0b" />
                <Bar dataKey="totalPenalty" stackId="a" fill="#dc2626" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Rankings Table */}
        <div className="card">
          <h4 className="mb-1">🏆 Officer Rankings</h4>
          <div className="table-container" style={{ maxHeight: 450, overflowY: 'auto' }}>
            <table style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Officer</th>
                  <th>Txs</th>
                  <th>Principal</th>
                  <th>Interest</th>
                  <th>Penalty</th>
                  <th>Total</th>
                  <th>Avg/Tx</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr><td colSpan="8" className="text-center text-muted p-3">No collection data yet.</td></tr>
                ) : data.map((officer, index) => (
                  <tr key={officer.officerId || index} style={{ background: index === 0 ? '#f0fdf4' : 'transparent' }}>
                    <td style={{ fontWeight: 700 }}>
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                    </td>
                    <td style={{ fontWeight: 600 }}>{officer.officerName}</td>
                    <td className="text-center">{officer.totalCollections}</td>
                    <td style={{ color: '#2563eb' }}>{formatCurrency(officer.totalPrincipal)}</td>
                    <td style={{ color: '#f59e0b' }}>{formatCurrency(officer.totalInterest)}</td>
                    <td style={{ color: '#dc2626' }}>{formatCurrency(officer.totalPenalty)}</td>
                    <td style={{ fontWeight: 700, color: '#059669' }}>{formatCurrency(officer.totalAmount)}</td>
                    <td className="text-muted">{formatCurrency(officer.avgAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}